import React, { useState } from "react";
import Swal from "sweetalert2";

import QuestionModal from "./QuestionModal";
import PagedTable from "./PagedTable";
import {
  downloadSampleQuestionsCsv,
  exportQuestionsCsv,
  parseQuestionsCsv,
} from "./questionHelpers";

const HEADERS = ["No", "Question Name", "Category", "Edit", "Delete"];
const SEARCH_KEYS = ["question_name", "category"];

function truncate(text, max = 80) {
  const s = String(text ?? "");
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

/** Question editor from old/admin/editor.php. */
export default function QuestionsTab({
  questions,
  isLoading,
  isSaving,
  onSaveQuestion,
  onDeleteQuestion,
  onImportCsv,
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);

  const openAdd = () => {
    setEditingQuestion(null);
    setModalOpen(true);
  };

  const openEdit = (q) => {
    setEditingQuestion(q);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingQuestion(null);
  };

  const handleModalSubmit = async (question) => {
    await onSaveQuestion({
      action: editingQuestion ? "edit" : "add",
      editId: editingQuestion?.id ?? null,
      question,
    });
    closeModal();
  };

  const handleDelete = async (q) => {
    const result = await Swal.fire({
      title: "Are you sure you want to delete this item?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#ef4444",
    });
    if (result.isConfirmed) await onDeleteQuestion(q.id);
  };

  const handleCsvUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!/\.csv$/i.test(file.name)) {
      Swal.fire({ icon: "error", title: "Error", text: "Please use CSV File" });
      return;
    }

    let rows;
    try {
      rows = parseQuestionsCsv(await file.text());
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: err?.message || "Failed to parse CSV." });
      return;
    }

    const confirm = await Swal.fire({
      title: "Upload CSV",
      text: `Questions will be overwritten with ${rows.length} question(s) from the file.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Upload",
      cancelButtonText: "Cancel",
    });
    if (confirm.isConfirmed) await onImportCsv(rows);
  };

  return (
    <div className="themeupdate-panel-wide quiz-panel">
      <div className="quiz-page-header">
        <h2 className="quiz-page-title">Add Questions</h2>
        <div className="quiz-toolbar">
          <button type="button" className="quiz-toolbar-btn" onClick={openAdd} disabled={isSaving}>
            <i className="fa-solid fa-plus" aria-hidden /> Add Question
          </button>
          <label className="quiz-toolbar-btn quiz-upload-label">
            <i className="fa-solid fa-upload" aria-hidden /> Upload CSV
            <input
              type="file"
              accept=".csv,text/csv"
              className="quiz-upload-input"
              onChange={handleCsvUpload}
              disabled={isSaving}
            />
          </label>
          <button
            type="button"
            className="quiz-toolbar-btn"
            onClick={() => exportQuestionsCsv(questions)}
            disabled={!questions.length}
          >
            <i className="fa-solid fa-file-export" aria-hidden /> Export CSV
          </button>
          <button type="button" className="quiz-toolbar-btn" onClick={downloadSampleQuestionsCsv}>
            <i className="fa-solid fa-download" aria-hidden /> Sample CSV
          </button>
        </div>
      </div>

      <p className="qa-char-limit-hint">
        * Questions will be overwritten on CSV upload. Use only CSV files with the columns
        question_name, category (Truth or Dare). Whitespace is removed automatically. Check the
        sample CSV for the correct format.
      </p>

      <PagedTable
        headers={HEADERS}
        rows={questions}
        searchKeys={SEARCH_KEYS}
        isLoading={isLoading}
        emptyText="No questions yet. Click Add Question or upload a CSV."
        renderCells={(q, no) => (
          <>
            <td>{no}</td>
            <td className="quiz-cell-question" title={q.question_name}>
              {truncate(q.question_name)}
            </td>
            <td>{q.category || "—"}</td>
            <td>
              <button
                type="button"
                className="quiz-table-btn quiz-table-btn-edit"
                onClick={() => openEdit(q)}
              >
                <i className="fa-solid fa-pen-to-square" aria-hidden /> Edit
              </button>
            </td>
            <td>
              <button
                type="button"
                className="quiz-table-btn quiz-table-btn-delete"
                onClick={() => handleDelete(q)}
                disabled={isSaving}
              >
                <i className="fa-solid fa-trash" aria-hidden /> Delete
              </button>
            </td>
          </>
        )}
      />

      <QuestionModal
        open={modalOpen}
        initial={editingQuestion}
        onClose={closeModal}
        onSubmit={handleModalSubmit}
        isSaving={isSaving}
      />
    </div>
  );
}
